import React, { useState, useEffect } from "react";

interface Lead {
  id?: number;
  business_name: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  category?: string;
  location?: string;
  source?: string;
  status?: string;
  contacted?: boolean;
  created_at?: string;
}

interface ScrapeJob {
  id: string;
  query: string;
  location: string;
  status: "queued" | "running" | "done" | "error";
  found: number;
  started_at?: string;
}

const LEAD_SOURCES = [
  { id: "google_maps", label: "Google Maps", icon: "🗺️", desc: "Scrape businesses from Google Maps in any UAE/GCC city" },
  { id: "yellow_pages", label: "Yellow Pages UAE", icon: "📒", desc: "yellowpages.ae business directory" },
  { id: "dubai_com",    label: "Dubai.com",          icon: "🌆", desc: "Dubai business listings & directories" },
  { id: "linkedin",     label: "LinkedIn",            icon: "💼", desc: "B2B decision-maker outreach (manual review)" },
  { id: "instagram",    label: "Instagram",           icon: "📸", desc: "Beauty salon & retailer discovery" },
];

const MOCK_LEADS: Lead[] = [
  { id: 1, business_name: "Glamour Beauty Salon", phone: "971501234567", category: "Beauty Salon", location: "Dubai Marina", source: "Google Maps", status: "new", contacted: false },
  { id: 2, business_name: "Pro Hair Studio",       phone: "971551234567", category: "Hair Salon",   location: "JLT",          source: "Google Maps", status: "contacted", contacted: true },
  { id: 3, business_name: "Nails & Spa Luxe",      phone: "971561234567", category: "Nail Salon",   location: "Downtown",     source: "Yellow Pages", status: "new", contacted: false },
  { id: 4, business_name: "The Beauty Bar",         phone: "971521234567", category: "Beauty Salon", location: "Business Bay", source: "Google Maps", status: "interested", contacted: true },
  { id: 5, business_name: "Elite Barber Lounge",    phone: "971581234567", category: "Barber Shop",  location: "Deira",        source: "Dubai.com",  status: "new", contacted: false },
];

const STATUS_COLORS: Record<string, string> = {
  new:         "#c8bfff",
  contacted:   "#ffd6a5",
  interested:  "#b8d58d",
  converted:   "#b8d58d",
  rejected:    "#ff6b6b",
};

const s: Record<string, React.CSSProperties> = {
  page: { padding: "24px", fontFamily: "'Space Grotesk', sans-serif", color: "#dae2fd" },
  card: {
    background: "rgba(19,27,46,0.7)", backdropFilter: "blur(14px)",
    border: "1px solid rgba(78,69,59,0.2)", borderRadius: 14, padding: "20px",
    marginBottom: 20,
  },
  heroCard: {
    background: "linear-gradient(135deg, rgba(11,19,38,0.95) 0%, rgba(20,10,40,0.9) 100%)",
    border: "1px solid rgba(200,191,255,0.15)",
    borderRadius: 16, padding: "28px",
    marginBottom: 24,
    position: "relative", overflow: "hidden",
  },
};

const AfaqLeadDashboard: React.FC = () => {
  const [tab,        setTab]        = useState<"scraper" | "leads" | "campaigns">("scraper");
  const [scrapeForm, setScrapeForm] = useState({ query: "beauty salon", location: "Dubai, UAE", source: "google_maps", limit: 30 });
  const [jobs,       setJobs]       = useState<ScrapeJob[]>([]);
  const [leads,      setLeads]      = useState<Lead[]>(MOCK_LEADS);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState("");
  const [sending,    setSending]    = useState<number | null>(null);
  const [todayCount, setTodayCount] = useState(0);   // WhatsApp outreach today (max 30)

  useEffect(() => {
    fetch("/api/leads/list", { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setLeads(d); })
      .catch(() => {/* use mock */});

    fetch("/api/leads/today-count", { credentials: "include" })
      .then(r => r.json())
      .then(d => setTodayCount(d.count || 0))
      .catch(() => {});
  }, []);

  const startScrape = async () => {
    const jobId = `job-${Date.now()}`;
    const newJob: ScrapeJob = {
      id: jobId,
      query: scrapeForm.query,
      location: scrapeForm.location,
      status: "running",
      found: 0,
      started_at: new Date().toISOString(),
    };
    setJobs(prev => [newJob, ...prev]);
    setLoading(true);

    try {
      const r = await fetch("/api/leads/scrape", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scrapeForm),
      });
      const data = await r.json();
      setJobs(prev => prev.map(j =>
        j.id === jobId ? { ...j, status: "done", found: data.found || data.leads?.length || 0 } : j
      ));
      if (data.leads) {
        setLeads(prev => [...data.leads, ...prev]);
      }
    } catch {
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: "error" } : j));
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsApp = async (lead: Lead) => {
    if (todayCount >= 30) {
      alert("Daily WhatsApp limit reached (30/day). Try again tomorrow.");
      return;
    }
    setSending(lead.id || 0);
    try {
      await fetch("/api/leads/outreach", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: lead.id, phone: lead.whatsapp || lead.phone }),
      });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, contacted: true, status: "contacted" } : l));
      setTodayCount(c => c + 1);
    } catch {
      alert("Failed to send. Check WhatsApp / UltraMsg connection.");
    } finally {
      setSending(null);
    }
  };

  const filteredLeads = leads.filter(l =>
    !search || l.business_name.toLowerCase().includes(search.toLowerCase()) ||
    l.location?.toLowerCase().includes(search.toLowerCase()) ||
    l.category?.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    { id: "scraper",   label: "Web Scraper",   icon: "🔍" },
    { id: "leads",     label: `Leads (${leads.length})`, icon: "🎯" },
    { id: "campaigns", label: "Campaigns",     icon: "📣" },
  ] as const;

  return (
    <div style={s.page}>
      {/* Hero */}
      <div style={s.heroCard}>
        {/* Background decoration */}
        <div style={{
          position: "absolute", top: -40, right: -40,
          width: 200, height: 200,
          background: "radial-gradient(circle, rgba(200,191,255,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{
            width: 64, height: 64,
            background: "linear-gradient(135deg, rgba(200,191,255,0.2), rgba(184,213,141,0.1))",
            border: "1px solid rgba(200,191,255,0.25)", borderRadius: 16,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30,
          }}>🔭</div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, fontFamily: "'Manrope', sans-serif", color: "#fff" }}>
              AfaqLead · B2B Intelligence
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#5a7090" }}>
              GCC lead generation · Web scraping · WhatsApp outreach (30/day limit)
            </p>
          </div>
          {/* Stats row */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
            {[
              { label: "Total Leads",    value: leads.length,                           color: "#c8bfff" },
              { label: "Contacted",      value: leads.filter(l => l.contacted).length,  color: "#ffd6a5" },
              { label: "Today's WA",     value: `${todayCount}/30`,                     color: todayCount >= 30 ? "#ff6b6b" : "#b8d58d" },
            ].map(stat => (
              <div key={stat.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: stat.color, fontFamily: "'Manrope', sans-serif" }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 10, color: "#3a5060" }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 18px",
              background: tab === t.id ? "rgba(200,191,255,0.15)" : "transparent",
              border: tab === t.id ? "1px solid rgba(200,191,255,0.3)" : "1px solid rgba(78,69,59,0.2)",
              borderRadius: 9, cursor: "pointer",
              color: tab === t.id ? "#c8bfff" : "#6a8090",
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 600,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Web Scraper ── */}
      {tab === "scraper" && (
        <div>
          {/* Source cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
            {LEAD_SOURCES.map(src => (
              <div
                key={src.id}
                onClick={() => setScrapeForm(f => ({ ...f, source: src.id }))}
                style={{
                  ...s.card, marginBottom: 0, cursor: "pointer",
                  border: scrapeForm.source === src.id
                    ? "1px solid rgba(200,191,255,0.4)"
                    : "1px solid rgba(78,69,59,0.2)",
                  background: scrapeForm.source === src.id
                    ? "rgba(200,191,255,0.06)"
                    : "rgba(19,27,46,0.7)",
                  transition: "all 0.18s",
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 6 }}>{src.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dae2fd", marginBottom: 4 }}>{src.label}</div>
                <div style={{ fontSize: 11, color: "#3a5060" }}>{src.desc}</div>
              </div>
            ))}
          </div>

          {/* Scrape config */}
          <div style={s.card}>
            <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#c8bfff" }}>Configure Scrape Job</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
              <div>
                <label style={{ display: "block", fontSize: 10, color: "#3a5060", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Business Type</label>
                <input
                  value={scrapeForm.query}
                  onChange={e => setScrapeForm(f => ({ ...f, query: e.target.value }))}
                  placeholder="e.g. beauty salon, barber"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.15)",
                    borderRadius: 8, padding: "9px 12px",
                    color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12,
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 10, color: "#3a5060", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Location</label>
                <input
                  value={scrapeForm.location}
                  onChange={e => setScrapeForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Dubai, UAE"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.15)",
                    borderRadius: 8, padding: "9px 12px",
                    color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12,
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 10, color: "#3a5060", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Max Results</label>
                <select
                  value={scrapeForm.limit}
                  onChange={e => setScrapeForm(f => ({ ...f, limit: Number(e.target.value) }))}
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.15)",
                    borderRadius: 8, padding: "9px 12px",
                    color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12,
                  }}
                >
                  {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n} results</option>)}
                </select>
              </div>
              <button
                onClick={startScrape}
                disabled={loading}
                style={{
                  background: "linear-gradient(135deg, rgba(200,191,255,0.2), rgba(184,213,141,0.15))",
                  border: "1px solid rgba(200,191,255,0.3)",
                  borderRadius: 9, padding: "9px 22px", cursor: "pointer",
                  color: "#c8bfff", fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                }}
              >
                {loading ? "⏳ Scraping…" : "🔍 Start Scrape"}
              </button>
            </div>

            {/* Daily limit warning */}
            <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(255,214,165,0.06)", borderRadius: 8, border: "1px solid rgba(255,214,165,0.1)", fontSize: 11, color: "#7a6050" }}>
              ⚠️ WhatsApp outreach is limited to <strong style={{ color: "#ffd6a5" }}>30 messages/day</strong> to prevent UltraMsg account bans.
              Today: {todayCount}/30 sent.
            </div>
          </div>

          {/* Job history */}
          {jobs.length > 0 && (
            <div style={s.card}>
              <h3 style={{ margin: "0 0 14px", fontSize: 13, color: "#c8bfff" }}>Recent Jobs</h3>
              {jobs.map(job => (
                <div key={job.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid rgba(78,69,59,0.08)" }}>
                  <div style={{ fontSize: 18 }}>
                    {job.status === "running" ? "⏳" : job.status === "done" ? "✅" : job.status === "error" ? "❌" : "📋"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#dae2fd" }}>{job.query} · {job.location}</div>
                    <div style={{ fontSize: 10, color: "#3a5060" }}>
                      {job.started_at ? new Date(job.started_at).toLocaleTimeString("en-AE") : ""} — {job.status}
                    </div>
                  </div>
                  {job.status === "done" && (
                    <span style={{ color: "#b8d58d", fontSize: 12, fontWeight: 700 }}>{job.found} found</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Leads Table ── */}
      {tab === "leads" && (
        <div style={s.card}>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 14, color: "#c8bfff", flex: 1 }}>Lead Database</h3>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search leads…"
              style={{
                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.12)",
                borderRadius: 8, padding: "7px 12px",
                color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, width: 200,
              }}
            />
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Business", "Location", "Category", "Source", "Status", "Action"].map(h => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#3a5060", textTransform: "uppercase", borderBottom: "1px solid rgba(78,69,59,0.15)" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => (
                  <tr key={lead.id}>
                    <td style={{ padding: "9px 10px", fontSize: 13, color: "#dae2fd", fontWeight: 600, borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      {lead.business_name}
                    </td>
                    <td style={{ padding: "9px 10px", fontSize: 12, color: "#6a8090", borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      {lead.location || "—"}
                    </td>
                    <td style={{ padding: "9px 10px", fontSize: 12, color: "#6a8090", borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      {lead.category || "—"}
                    </td>
                    <td style={{ padding: "9px 10px", fontSize: 11, color: "#4a6070", borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      {lead.source || "—"}
                    </td>
                    <td style={{ padding: "9px 10px", borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                        background: `${STATUS_COLORS[lead.status || "new"] || "#c8bfff"}15`,
                        border: `1px solid ${STATUS_COLORS[lead.status || "new"] || "#c8bfff"}30`,
                        borderRadius: 5, padding: "2px 8px",
                        color: STATUS_COLORS[lead.status || "new"] || "#c8bfff",
                        textTransform: "capitalize",
                      }}>{lead.status || "new"}</span>
                    </td>
                    <td style={{ padding: "9px 10px", borderBottom: "1px solid rgba(78,69,59,0.07)" }}>
                      {!lead.contacted ? (
                        <button
                          onClick={() => sendWhatsApp(lead)}
                          disabled={sending === lead.id || todayCount >= 30}
                          style={{
                            background: "rgba(37,211,102,0.12)",
                            border: "1px solid rgba(37,211,102,0.25)",
                            borderRadius: 7, padding: "5px 12px", cursor: "pointer",
                            color: "#25d366", fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: 11, fontWeight: 700,
                          }}
                        >
                          {sending === lead.id ? "⏳" : "💬 WA"}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: "#3a5060" }}>✅ Sent</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Campaigns ── */}
      {tab === "campaigns" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, color: "#c8bfff" }}>Outreach Campaigns</h3>
          <p style={{ fontSize: 12, color: "#3a5060", marginBottom: 16 }}>
            Bulk WhatsApp campaigns to segmented lead lists. Respects the 30/day limit automatically.
          </p>
          {[
            { name: "Beauty Salons — Dubai",      target: 30, sent: todayCount, status: "Active",   color: "#b8d58d" },
            { name: "Barber Shops — Deira",       target: 30, sent: 0,          status: "Scheduled", color: "#ffd6a5" },
            { name: "Nail Salons — Abu Dhabi",    target: 30, sent: 0,          status: "Draft",     color: "#c8bfff" },
          ].map(campaign => (
            <div key={campaign.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid rgba(78,69,59,0.1)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#dae2fd" }}>{campaign.name}</div>
                <div style={{ fontSize: 11, color: "#3a5060", marginTop: 3 }}>
                  {campaign.sent}/{campaign.target} sent
                  <div style={{ marginTop: 4, height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, width: 150, display: "inline-block", verticalAlign: "middle", marginLeft: 8 }}>
                    <div style={{ height: "100%", width: `${(campaign.sent / campaign.target) * 100}%`, background: campaign.color, borderRadius: 2 }} />
                  </div>
                </div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                background: `${campaign.color}15`, border: `1px solid ${campaign.color}30`,
                borderRadius: 5, padding: "2px 9px", color: campaign.color,
              }}>{campaign.status}</span>
            </div>
          ))}
          <button
            onClick={() => alert("Campaign builder — connect feat/afaqlead-b2b backend")}
            style={{
              marginTop: 16, background: "rgba(200,191,255,0.1)", border: "1px solid rgba(200,191,255,0.25)",
              borderRadius: 9, padding: "9px 20px", cursor: "pointer",
              color: "#c8bfff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 700,
            }}
          >
            + New Campaign
          </button>
        </div>
      )}
    </div>
  );
};

export default AfaqLeadDashboard;
